import { defineStore } from "pinia";
import { SocialMediaPage, SocialMediaType, getSocialMediaTypes } from "@/services/pages";
import { useUserStore } from "@/store/user";
import { useRealtimeStore } from "./realtime";
import { delay } from "@/utils/delay";
import { CommentWatchlist, getAICommentTags, getCommentWatchlists, Tag } from "@/services/comments";
import { PlatformKey, getPlatforms } from "@/services/cmt";

type ApplicationStore = {
  socialMediaTypes: SocialMediaType[] | null;
  commentWatchlists: CommentWatchlist[];
  platforms: string[] | null;
  pages: SocialMediaPage[];
  isInitialized: boolean;
  showHelperTooltips: boolean;
  commentAITags: { tags: Tag[] };
};

export type SocialMediaPlatformParameters = {
  socialMediaTypeId: number;
  socialMediaConnectionId: number;
  pageExternalId?: string;
};

export const useApplicationStore = defineStore("application", {
  state: (): ApplicationStore => ({
    socialMediaTypes: null,
    commentWatchlists: [],
    platforms: null,
    pages: [],
    isInitialized: false,
    showHelperTooltips: false,
    commentAITags: { tags: [] },
  }),
  getters: {
    cmtPlatforms(state) {
      const platforms = getPlatforms();
      const threadedPlatformKeys = new Set<string>([
        PlatformKey.INSTAGRAM,
        PlatformKey.FACEBOOK,
        PlatformKey.YOUTUBE,
        PlatformKey.TIKTOK,
      ]);

      return platforms.map((platform) => {
        const platformTypeId = this.socialMediaTypes?.find((st) => {
          const type = st.type.toLowerCase();
          return type === platform.key || type === platform.label.toLowerCase();
        })?.id ?? -1;

        const watchlists = this.commentWatchlistsByPlatform(platformTypeId).map((wl) => ({
          id: wl.id,
          name: wl.name,
          filters: {},
          isPublic: wl.isPublic,
        }));

        if (platform.features.comments) {
          platform.features.comments.watchlists = watchlists;
        }

        if (platform.features.threads) {
          platform.features.threads.watchlists = watchlists;
        } else if (threadedPlatformKeys.has(platform.key) && platform.features.comments) {
          platform.features.threads = {
            threads: true,
            folders: [...(platform.features.comments.folders ?? [])],
            watchlists,
          };
        }

        return platform;
      });
    },
    commentWatchlistsByPlatform (state) {
      return (socialMediaType: number) => state.commentWatchlists.filter(wl => wl.smt === socialMediaType);
    },
    socialMediaPlatformParameter(state) {
      console.log("socialMediaPlatformParameter getter called");
      return (platformKey: string): SocialMediaPlatformParameters => {
        const socialMediaType =
          state.socialMediaTypes?.find((s) => s.type.toLowerCase() === platformKey) ?? null;
        return {
          socialMediaTypeId: socialMediaType?.id ?? -1,
          socialMediaConnectionId: socialMediaType?.socialMediaConnections[0].id ?? -1,
          pageExternalId: socialMediaType?.connectedPages[0]?.externalId ?? "",
        }
      };
    },
    socialMediaTypeById(state) {
      return (socialMediaTypeId: number) =>
        state.socialMediaTypes?.find((s) => s.id === socialMediaTypeId);
    },
    expiredSocialMediaPages(state) {
      // note: youtube pages are currently ignored as they don't have token expiration
      // tiktok now properly returns RefreshTokenExpiresAt (365 days) as tokenValidUntil
      return state.pages.filter(
        (p) =>
          !(p.socialMediaType.toLowerCase() === PlatformKey.YOUTUBE) &&
          (!p.tokenValidUntil || +new Date(p.tokenValidUntil) < +new Date()),
      );
    },
    socialMediaPagesByPlatform(state) {
      return (platformKey: string) =>
        state.socialMediaTypes
          ?.filter((s) => s.type.toLowerCase() === platformKey)
          .flatMap((s) => s.connectedPages ?? []) ?? [];
    },
  },
  actions: {
    async fetchPages() {
      try {
        const socialMediaTypes = await getSocialMediaTypes();
        this.socialMediaTypes = socialMediaTypes.map(smt => ({...smt, connectedPages: smt.connectedPages ?? []}));
        this.platforms = this.socialMediaTypes.map((socialMediaType) =>
          socialMediaType.type.toLowerCase(),
        );

        this.pages = this.socialMediaTypes
          .filter((type) => type.connectedPages)
          .flatMap((p) => p.connectedPages);
      } catch (error) {
        console.log(error);
      }
      return this.pages;
    },
    async fetchCommentWatchlists() {
      const unsortedCommentWatchlists = await getCommentWatchlists();
      const publicWatchlists = unsortedCommentWatchlists.filter((watchlist) => watchlist.isPublic);

      const privateWatchlists = unsortedCommentWatchlists.filter(
        (watchlist) => !watchlist.isPublic,
      );
      this.commentWatchlists = publicWatchlists.concat(privateWatchlists);
    },
    async initializeApplication() {
      const userStore = useUserStore();
      await userStore.fetchUser();
      const realtimeStore = useRealtimeStore();
      this.isInitialized = false;
      const startTime = performance.now();
      await this.fetchPages();
      await this.fetchCommentWatchlists();
      this.commentAITags = await getAICommentTags();

      try {
        await realtimeStore.connect();
      } catch (error) {
        console.error(error);
        console.warn("no status updates possible due to missing realtime service connection.");
      }
      const finishTime = performance.now();
      const timeElapsed = finishTime - startTime;
      if (timeElapsed < 1500) {
        await delay(1500 - timeElapsed);
      }
      this.isInitialized = true;
    },
  },
});
